import * as Sentry from "@sentry/vue";
import { App } from "vue";

import { Router } from "vue-router";
import { config } from "../../config/config";
import { GenericFailure } from "../../errors/GenericFailure";
import { assertNever } from "../../util/assertNever";
import { LogLevel, mapLogLevelToSentrySeverityLevel } from "./enum.logLevel";

let isPageUnloading = false;

export type LogContext = Record<string, { [key: string]: unknown }>;
interface InitializeLoggingParams {
  app: App<Element>;
  router: Router;
}
export function initializeLogging({ app, router }: InitializeLoggingParams): void {
  Sentry.init({
    enabled: config.isProduction,
    app,
    dsn: config.sentry.dsn,
    environment: config.isProduction ? "production" : "development",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
      Sentry.globalHandlersIntegration({
        onerror: false,
        onunhandledrejection: false,
      }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    tracesSampleRate: 0.05,
    normalizeDepth: 5,

    tracePropagationTargets: ["https://api.cloud.anex.is", "https://api.cloud.anexis.localhost:10000"],
  });
}

type LogParams<ErrorType = unknown> = {
  error: ErrorType;
  context?: undefined | Record<string, { [key: string]: unknown }>;
  logLevel?: undefined | LogLevel;
};
export function log({ error, context, logLevel }: LogParams): void {
  if (isPageUnloading) {
    return;
  }

  if (error instanceof PromiseRejectionEvent) {
    return log({
      error: error.reason,
      logLevel,
      context,
    });
  }

  if (error instanceof GenericFailure) {
    if (error.logContext !== undefined) {
      context = {
        ...error.logContext,
        ...context,
      };
    }
    if (logLevel === undefined && error.logLevel !== undefined) {
      logLevel = error.logLevel;
    }
  }

  Sentry.withScope((scope) => {
    for (const key in context) {
      scope.setContext(key, context[key]);
    }

    if (typeof error === "string") {
      logMessage({
        error,
        logLevel,
      });
    } else if (error instanceof Error) {
      logException({
        error,
        logLevel,
      });
    }
  });
}

function logException({ error, logLevel = LogLevel.ERROR }: Omit<LogParams<Error>, "context">) {
  Sentry.captureException(error, {
    level: mapLogLevelToSentrySeverityLevel(logLevel),
  });

  if (!config.isProduction) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function logMessage({ error, logLevel = LogLevel.ERROR }: LogParams<string>) {
  Sentry.captureMessage(error, {
    level: mapLogLevelToSentrySeverityLevel(logLevel),
  });

  if (!config.isProduction) {
    switch (logLevel) {
      case LogLevel.DEBUG:
        // eslint-disable-next-line no-console
        console.debug(error);
        break;

      case LogLevel.INFO:
        // eslint-disable-next-line no-console
        console.log(error);
        break;

      case LogLevel.WARNING:
        // eslint-disable-next-line no-console
        console.warn(error);
        break;

      case LogLevel.ERROR:
      case LogLevel.FATAL:
        // eslint-disable-next-line no-console
        console.error(error);
        break;

      default:
        assertNever(logLevel);
    }
  }
}

window.addEventListener("beforeunload", () => {
  isPageUnloading = true;
});
